import { DownloadFileType } from '@anytag/shared-types';
import { getAccountManagementApiUrl } from '@anytag/shared-utils';
import { DownloadButton, DownloadButtonProps } from '@anytag/shared-atoms';
import { ExportTagsModalProps, ExportTagsModal } from '@anytag/shared-molecules';
import { useToggleState, useDownloadFile, useQueryHelper } from '@anytag/shared-hooks';
import { useExportInstagramHashtagPostsToGoogleSpreadsheetMutation } from '@anytag/graphql';

interface Props {
  hashtagId: string;
}

export const useExportInstagramHashtagPosts = ({ hashtagId }: Props) => {
  const exportModal = useToggleState();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { loading, callExportInstagramHashtagPostsToGoogleSpreadsheet } =
    useExportInstagramHashtagPostsToGoogleSpreadsheetMutation();

  const handleExportFile: ExportTagsModalProps['onSubmit'] = async ({ type, endDate, startDate }) => {
    try {
      const isSupportedType = [DownloadFileType.CSV, DownloadFileType.EXCEL, DownloadFileType.SPREADSHEET];

      if (!hashtagId || !isSupportedType) return;

      if (type === DownloadFileType.SPREADSHEET) {
        await callExportInstagramHashtagPostsToGoogleSpreadsheet({
          variables: {
            input: {
              hashtagId: +hashtagId,
              endDate: endDate.toString(),
              startDate: startDate.toString()
            }
          }
        });
        enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
      } else {
        await handleDownloadFile({
          method: 'GET',
          params: { endDate, startDate },
          filenamePrefix: `hashtags-analytics-${new Date().toUTCString()}`,
          url: getAccountManagementApiUrl(
            `api/analytics/instagram/hashtag/${type === DownloadFileType.EXCEL ? 'xlsx' : 'csv'}/${hashtagId}`
          )
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || t('failedToDownload'), { variant: 'error' });
    } finally {
      exportModal.close();
    }
  };

  return {
    ExportButton: (props: DownloadButtonProps) => (
      <DownloadButton onClick={exportModal.open} title={t('Button.HashTag Export')} {...props} />
    ),
    ExportModal: (
      <ExportTagsModal
        open={exportModal.status}
        onClose={exportModal.close}
        onSubmit={handleExportFile}
        downloading={downloading || loading}
      />
    )
  };
};
